import { staticPageMetadata } from '../../lib/i18n/staticPageMetadata';
import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, TextInput, KeyboardAvoidingView } from 'react-native';
import { Stack, Link } from 'expo-router';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useState, useEffect } from 'react';
import { useSession } from '../../lib/SessionContext';
import { supabase, toError } from '../../lib/supabase';
import MetaTags from '../../components/MetaTags';
import colors from '../../lib/colors';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, useOptionalResolvedMessage } from '../../lib/i18n/messages';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

export default function UpgradeScreen() {
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const { session } = useSession();
    const [selectedPlan, setSelectedPlan] = useState<'pay-per-use' | 'subscription' | null>(null);
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [uploadError, setUploadError] = useState<LocalizedMessage | null>(null);
    const resolvedUploadError = useOptionalResolvedMessage(uploadError);
    const comparisonFeatures = [
        msg({ id: 'upgrade.feature.aiSummary', message: 'AI-generated summary' }),
        msg({ id: 'upgrade.feature.qualityRating', message: 'Quality rating (1-5 stars)' }),
        msg({ id: 'upgrade.feature.weaknesses', message: 'Identified weaknesses' }),
        msg({ id: 'upgrade.feature.conflictCheck', message: 'Basic conflict of interest check' }),
        msg({ id: 'upgrade.feature.factCheckedCitations', message: 'Fact-checked citations' }),
        msg({ id: 'upgrade.feature.authorResearch', message: 'Author background research' }),
        msg({ id: 'upgrade.feature.fundingAnalysis', message: 'Funding source analysis' }),
        msg({ id: 'upgrade.feature.advancedReasoning', message: 'Advanced AI reasoning' }),
        msg({ id: 'upgrade.feature.methodologyCritique', message: 'Detailed methodology critique' }),
        msg({ id: 'upgrade.feature.exportReport', message: 'Export detailed report' }),
    ];
    const selectedPlanLabel = selectedPlan === 'pay-per-use'
        ? i18n._(msg({ id: 'upgrade.plan.payPerAnalysis', message: 'Pay Per Analysis' }))
        : i18n._(msg({ id: 'upgrade.plan.monthlySubscription', message: 'Monthly Subscription' }));
    const usdPrice = new Intl.NumberFormat(formatLocale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    useEffect(() => {
        if (session?.user?.email) {
            setEmail(session.user.email);
        }
    }, [session]);

    const handleJoinWaitlist = async () => {
        setUploadError(null);

        if (!email.trim()) {
            setUploadError(msg({ id: 'upgrade.emailRequired', message: 'Email is required' }));
            return;
        }

        if (!/\S+@\S+\.\S+/.test(email)) {
            setUploadError(msg({ id: 'upgrade.validEmailRequired', message: 'Please enter a valid email address' }));
            return;
        }

        if (!selectedPlan) {
            setUploadError(msg({ id: 'upgrade.planRequired', message: 'Please select a plan' }));
            return;
        }

        try {
            const { error } = await supabase
                .from('waitlist')
                .insert({
                    email: email,
                    plan_preference: selectedPlan
                });

            if (error) throw toError(error);

            setSubmitted(true);
        } catch (error: any) {
            console.error('Error joining waitlist:', error);
            // Simple error feedback for web compatibility
            setUploadError(msg({ id: 'upgrade.waitlistError', message: 'Sorry, there was an error joining the waitlist. Please try again.' }));
            setTimeout(() => setUploadError(null), 4000);
        }
    };

    return (
        <>
            <MetaTags surfaceId="upgrade" />
            <Stack.Screen options={{ headerShown: false }} />
            <KeyboardAvoidingView
                style={styles.container}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                keyboardVerticalOffset={Platform.OS === 'ios' ? 120 : 40}
            >
                <Header />
                <ScrollView
                    style={styles.scrollView}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                    contentContainerStyle={{ paddingBottom: 150 }}
                >
                    <View style={styles.content}>
                        <Link href="/" asChild>
                            <TouchableOpacity style={styles.backLink}>
                                <Text style={styles.backLinkText}><Trans id="upgrade.backToHome" comment="UI copy in app/(public)/upgrade.tsx">← Back to home</Trans></Text>
                            </TouchableOpacity>
                        </Link>

                        <Text
                            style={styles.title}
                            accessibilityRole="header"
                            {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                        ><Trans id="upgrade.upgradeToPaperzillaPro" comment="UI copy in app/(public)/upgrade.tsx">
                            Upgrade to Paperzilla Pro
                        </Trans></Text>
                        <Text style={styles.subtitle}><Trans id="upgrade.getDeeperInsightsWithFactCheckedCitationsAuthorBackgroundResearchAnd" comment="UI copy in app/(public)/upgrade.tsx">
                            Get deeper insights with fact-checked citations, author background research, and advanced AI analysis.
                        </Trans></Text>

                        {/* Feature Comparison Table */}
                        <View style={styles.comparisonSection}>
                            <Text
                                style={styles.sectionTitle}
                                accessibilityRole="header"
                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                            >
                                <Trans id="upgrade.whatsIncluded">What's Included</Trans>
                            </Text>
                            <View style={styles.table}>
                                <View style={styles.tableHeader}>
                                    <View style={styles.featureColumn}>
                                        <Text style={styles.tableHeaderText}><Trans id="upgrade.featureColumn">Feature</Trans></Text>
                                    </View>
                                    <View style={styles.planColumn}>
                                        <Text style={styles.tableHeaderText}><Trans id="pricing.plan.starter.name">Starter</Trans></Text>
                                    </View>
                                    <View style={styles.planColumn}>
                                        <Text style={[styles.tableHeaderText, styles.proHeader]}><Trans id="pricing.plan.pro.name">Pro</Trans></Text>
                                    </View>
                                </View>

                                {comparisonFeatures.map((feature, index) => renderFeatureRow(
                                    i18n._(feature),
                                    index < 4,
                                    true,
                                    String(feature.id),
                                ))}
                            </View>
                        </View>

                        {/* Pricing Options */}
                        <View style={styles.pricingSection}>
                            <Text
                                style={styles.sectionTitle}
                                accessibilityRole="header"
                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                            >
                                <Trans id="upgrade.choosePlan">Choose Your Plan</Trans>
                            </Text>
                            <Text style={styles.pricingSubtitle}>
                                <Trans id="upgrade.planPreferencePrompt">Help us understand what works best for you:</Trans>
                            </Text>
                            <TouchableOpacity
                                style={[
                                    styles.pricingCard,
                                    selectedPlan === 'pay-per-use' && styles.selectedCard
                                ]}
                                onPress={() => setSelectedPlan('pay-per-use')}
                                activeOpacity={0.8}
                            >
                                <View style={styles.cardHeader}>
                                    <Text style={styles.planName}><Trans id="upgrade.plan.payPerAnalysis">Pay Per Analysis</Trans></Text>
                                </View>
                                <View style={styles.priceRow}>
                                    <Text style={styles.price}>
                                        {usdPrice.format(25)}<Text style={styles.priceUnit}><Trans id="upgrade.price.perAnalysis">/analysis</Trans></Text>
                                    </Text>
                                    <View style={styles.radioCircle}>
                                        {selectedPlan === 'pay-per-use' && <View style={styles.radioInner} />}
                                    </View>
                                </View>
                                <Text style={styles.planDescription}>
                                    <Trans id="upgrade.plan.payPerAnalysisDescription">Perfect for occasional use. Pay only when you need a deep dive into a paper.</Trans>
                                </Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                                style={[
                                    styles.pricingCard,
                                    selectedPlan === 'subscription' && styles.selectedCard
                                ]}
                                onPress={() => setSelectedPlan('subscription')}
                                activeOpacity={0.8}
                            >
                                <View style={styles.inlineBadge}>
                                    <Text style={styles.savingsText}><Trans id="upgrade.plan.bestValue">Best Value</Trans></Text>
                                </View>

                                <View style={styles.cardHeader}>
                                    <Text style={styles.planName}><Trans id="upgrade.plan.monthlySubscription">Monthly Subscription</Trans></Text>
                                </View>
                                <View style={styles.priceRow}>
                                    <Text style={styles.price}>
                                        {usdPrice.format(100)}<Text style={styles.priceUnit}><Trans id="upgrade.price.perMonth">/month</Trans></Text>
                                    </Text>
                                    <View style={styles.radioCircle}>
                                        {selectedPlan === 'subscription' && <View style={styles.radioInner} />}
                                    </View>
                                </View>
                                <Text style={styles.planDescription}>
                                    <Trans id="upgrade.plan.monthlyDescription">Includes 8 analyses per month. Save 50% compared to pay-per-use!</Trans>
                                </Text>
                            </TouchableOpacity>
                        </View>

                        {/* Waitlist Form */}
                        {!submitted ? (
                            <View style={styles.waitlistSection}>
                                <Text
                                    style={styles.sectionTitle}
                                    accessibilityRole="header"
                                    {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                                ><Trans id="upgrade.joinTheWaitlist" comment="UI copy in app/(public)/upgrade.tsx">
                                    Join the Waitlist
                                </Trans></Text>
                                {uploadError && (
                                    <View style={styles.errorContainer}>
                                        <Text style={styles.errorText}>{resolvedUploadError}</Text>
                                    </View>
                                )}
                                <Text style={styles.waitlistText}><Trans id="upgrade.proFeaturesAreComingSoonBeTheFirstToKnow" comment="UI copy in app/(public)/upgrade.tsx">
                                    Pro features are coming soon! Be the first to know when we launch.
                                </Trans></Text>

                                <TextInput
                                    style={styles.emailInput}
                                    placeholder={i18n._(msg({ id: 'upgrade.emailPlaceholder', message: 'Enter your email' }))}
                                    value={email}
                                    onChangeText={setEmail}
                                    keyboardType="email-address"
                                    autoCapitalize="none"
                                    autoComplete="email"
                                    textContentType="emailAddress"
                                    returnKeyType="go"
                                    onSubmitEditing={handleJoinWaitlist}
                                />

                                <TouchableOpacity
                                    style={[
                                        styles.submitButton,
                                        !selectedPlan && styles.submitButtonDisabled
                                    ]}
                                    onPress={handleJoinWaitlist}
                                    disabled={!selectedPlan}
                                >
                                    <Text style={styles.submitButtonText}>
                                        {selectedPlan ? <Trans id="upgrade.joinWaitlist" comment="Conditional UI copy in app/(public)/upgrade.tsx">Join Waitlist</Trans> : <Trans id="upgrade.selectAPlanAbove" comment="Conditional UI copy in app/(public)/upgrade.tsx">Select a plan above</Trans>}
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        ) : (
                            <View style={styles.successMessage}>
                                <Text style={styles.successTitle}><Trans id="upgrade.youReOnTheList" comment="UI copy in app/(public)/upgrade.tsx">You're on the list!</Trans></Text>
                                <Text style={styles.successText}><Trans id="upgrade.thanksForYourInterestInPaperzillaProWeLlEmailYou" comment="UI copy in app/(public)/upgrade.tsx">
                                    Thanks for your interest in Paperzilla Pro. We'll email you as soon as it's ready.
                                </Trans></Text>
                                <Text style={styles.selectedPlanText}><Trans id="upgrade.yourPreference" comment="UI copy in app/(public)/upgrade.tsx">
                                    Your preference: {selectedPlanLabel}
                                </Trans></Text>
                            </View>
                        )}

                        <Footer />
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>
        </>
    );
}

function renderFeatureRow(feature: string, starter: boolean, pro: boolean, key: string) {
    return (
        <View key={key} style={styles.tableRow}>
            <View style={styles.featureColumn}>
                <Text style={styles.featureText}>{feature}</Text>
            </View>
            <View style={styles.planColumn}>
                <Text style={styles.checkmark}>{starter ? '✓' : '–'}</Text>
            </View>
            <View style={styles.planColumn}>
                <Text style={[styles.checkmark, styles.proCheckmark]}>{pro ? '✓' : '–'}</Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fafafa',
        ...(Platform.OS === 'web' && {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
            marginHorizontal: 'auto',
        }),
    },
    scrollView: {
        flex: 1,
    },
    content: {
        padding: 20,
        paddingBottom: 40,
    },
    backLink: {
        marginBottom: 24,
    },
    backLinkText: {
        fontSize: 16,
        color: colors.primaryGreen,
        fontWeight: '500',
    },
    title: {
        fontSize: 32,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        marginBottom: 12,
    },
    subtitle: {
        fontSize: 18,
        color: '#4b5563',
        lineHeight: 28,
        marginBottom: 40,
    },
    sectionTitle: {
        fontSize: 24,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        marginBottom: 20,
    },
    comparisonSection: {
        marginBottom: 48,
    },
    table: {
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        overflow: 'hidden',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f9fafb',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
    },
    featureColumn: {
        flex: 2,
        padding: 16,
    },
    planColumn: {
        flex: 1,
        padding: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    tableHeaderText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
        textAlign: 'center',
    },
    proHeader: {
        color: colors.primaryGreen,
    },
    featureText: {
        fontSize: 14,
        color: '#4b5563',
    },
    checkmark: {
        fontSize: 18,
        color: '#9ca3af',
    },
    proCheckmark: {
        color: colors.primaryGreen,
        fontWeight: 'bold',
    },
    pricingSection: {
        marginBottom: 48,
    },
    pricingSubtitle: {
        fontSize: 16,
        color: '#6b7280',
        marginBottom: 20,
    },
    selectedCard: {
        borderColor: colors.primaryGreen,
        backgroundColor: '#f9fdfb',
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    priceRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    planName: {
        fontSize: 20,
        fontWeight: '600',
        color: colors.primaryGreenDark,
    },
    radioCircle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#d1d5db',
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioInner: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: colors.primaryGreen,
    },
    price: {
        fontSize: 36,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        marginBottom: 8,
    },
    priceUnit: {
        fontSize: 16,
        fontWeight: 'normal',
        color: '#6b7280',
    },
    planDescription: {
        fontSize: 16,
        color: '#4b5563',
        lineHeight: 24,
    },
    pricingCard: {
        borderWidth: 2,
        borderColor: '#e5e7eb',
        borderRadius: 12,
        padding: 24,
        marginBottom: 16,
        backgroundColor: 'white',
        position: 'relative',
        marginTop: 12,
    },
    inlineBadge: {
        position: 'absolute',
        top: -12,
        left: '50%',
        transform: [{ translateX: -50 }],
        backgroundColor: colors.primaryGreen,
        paddingHorizontal: 16,
        paddingVertical: 6,
        borderRadius: 12,
        zIndex: 1,
    },
    savingsText: {
        color: 'white',
        fontSize: 12,
        fontWeight: '600',
    },

    waitlistSection: {
        marginBottom: 48,
    },
    waitlistText: {
        fontSize: 16,
        color: '#6b7280',
        marginBottom: 20,
    },
    emailInput: {
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 8,
        padding: 16,
        fontSize: 16,
        backgroundColor: 'white',
        marginBottom: 16,
    },
    submitButton: {
        backgroundColor: colors.primaryGreen,
        paddingVertical: 16,
        borderRadius: 8,
        alignItems: 'center',
    },
    submitButtonDisabled: {
        backgroundColor: '#d1d5db',
    },
    submitButtonText: {
        color: 'white',
        fontSize: 18,
        fontWeight: '600',
    },
    successMessage: {
        backgroundColor: '#f0fdf4',
        borderWidth: 1,
        borderColor: colors.veryLightGreen,
        borderRadius: 12,
        padding: 24,
        marginBottom: 48,
        alignItems: 'center',
    },
    successTitle: {
        fontSize: 24,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        marginBottom: 8,
    },
    successText: {
        fontSize: 16,
        color: '#4b5563',
        textAlign: 'center',
        marginBottom: 8,
    },
    selectedPlanText: {
        fontSize: 14,
        color: colors.primaryGreen,
        fontWeight: '500',
    },
    errorContainer: {
        backgroundColor: '#fef2f2',
        borderWidth: 1,
        borderColor: '#fecaca',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
    },
    errorText: {
        color: '#dc2626',
        fontSize: 14,
        textAlign: 'center',
    },
});

export function generateMetadata() {
    return staticPageMetadata({ surfaceId: 'upgrade' });
}
